import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';

const TARGET_ROW_CLASS = 'context-menu-target-row';
const VIEWPORT_MARGIN = 8;

@Component({
  selector: 'app-right-click-menu',
  templateUrl: './right-click-menu.component.html',
  styleUrls: ['./right-click-menu.component.scss']
})
export class RightClickMenuComponent implements OnChanges {
  @Input() visible = false;
  @Input() x = 0;
  @Input() y = 0;
  @Input() item: any = null;
  @Input() actions: { label?: string; value?: string; separator?: boolean; indent?: boolean }[] = [];

  @Output() action = new EventEmitter<{ value: string; item: any }>();
  @Output() close = new EventEmitter<void>();

  @ViewChild('menuEl') menuEl?: ElementRef<HTMLElement>;

  // What the template actually positions itself at — starts at the click point, then gets
  // pulled back on-screen once the menu's real (possibly scroll-capped) size is known.
  adjustedX = 0;
  adjustedY = 0;

  // True only during the measure pass below — the menu is rendered (so getBoundingClientRect
  // reflects its real, possibly scroll-capped size) but kept invisible so the user never sees
  // it jump from the raw click point to its clamped position.
  measuring = false;

  private targetRow: Element | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges) {
    // Re-run on every change, not just `visible` — right-clicking a different row while
    // the menu is already open re-fires with new x/y/item but `visible` stays true the
    // whole time (it's reset synchronously before change detection runs), so relying on
    // changes['visible'] alone would miss re-targeting the newly clicked row.
    if (this.visible) {
      this.highlightTargetRow();
      this.positionMenu();
    } else {
      this.clearTargetRow();
    }
  }

  // A long action list (e.g. one row per editor tab) can be taller than the viewport even
  // after CSS caps it to 80vh — repositioning by the click point alone can still leave most
  // of it hanging off the bottom/right edge. Measuring synchronously (via detectChanges,
  // rather than waiting for the next automatic tick) means the clamp is guaranteed to have
  // applied before the user sees anything, instead of depending on timing.
  private positionMenu() {
    this.adjustedX = this.x;
    this.adjustedY = this.y;
    this.measuring = true;
    this.cdr.detectChanges();

    const el = this.menuEl?.nativeElement;
    if (el) {
      const rect = el.getBoundingClientRect();

      if (rect.right > window.innerWidth) {
        this.adjustedX = Math.max(VIEWPORT_MARGIN, window.innerWidth - rect.width - VIEWPORT_MARGIN);
      }
      if (rect.bottom > window.innerHeight) {
        this.adjustedY = Math.max(VIEWPORT_MARGIN, window.innerHeight - rect.height - VIEWPORT_MARGIN);
      }
    }

    this.measuring = false;
    this.cdr.detectChanges();
  }

  // Finds the table row under the original right-click point and highlights it, so the
  // menu's target is obvious regardless of which table it's used from — no per-table wiring needed.
  private highlightTargetRow() {
    this.clearTargetRow();

    // Right-clicking a new row while the menu is already open re-targets before this
    // component's own template re-renders, so the previous menu can still be sitting
    // (visually stale) exactly at the new click point. elementFromPoint (singular) would
    // hit that stale menu instead of the row underneath it. elementsFromPoint returns the
    // whole stack at that point, so we can look past the menu to the real row beneath it.
    const stack = document.elementsFromPoint(this.x, this.y);
    const row = stack
      .map(el => el.closest('tr'))
      .find((tr): tr is HTMLTableRowElement => !!tr && !tr.closest('.context-menu'));

    if (!row) return;

    row.classList.add(TARGET_ROW_CLASS);
    this.targetRow = row;
  }

  private clearTargetRow() {
    this.targetRow?.classList.remove(TARGET_ROW_CLASS);
    this.targetRow = null;
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    if (!this.visible) return;

    const target = event.target as HTMLElement;
    const clickedInsideMenu = target.closest('.context-menu');

    if (!clickedInsideMenu) {
      this.visible = false;
      this.clearTargetRow();
      this.close.emit();
    }
  }

  @HostListener('window:focus')
  onWindowFocus() {
    if (this.visible) {
      this.visible = false;
      this.clearTargetRow();
      this.close.emit();
    }
  }

  getActionIcon(value: string | undefined): string {
    const iconMap: { [key: string]: string } = {
      'open': 'fas fa-fw fa-external-link',
      'edit': 'fas fa-fw fa-edit',
      'view': 'fas fa-fw fa-eye',
      'import': 'fas fa-fw fa-download',
      'delete': 'fas fa-fw fa-trash',
      'copy': 'fas fa-fw fa-copy',
      'login_as': 'fas fa-fw fa-key',
      'open_media_order': 'fas fa-fw fa-receipt',
      'create_media_order': 'fas fa-fw fa-plus',
      'add_to_media_order': 'fas fa-fw fa-plus',
      'open_library': 'fas fa-fw fa-folder-open',
      'upload': 'fas fa-fw fa-cloud-upload',
      'sync_from_wordpress': 'fas fa-fw fa-download',
      'clone': 'fas fa-fw fa-clone',
      'credit_note': 'fas fa-fw fa-file-invoice-dollar',
      'compensation': 'fas fa-fw fa-hand-holding-dollar',
      'loading': 'fas fa-fw fa-spinner fa-spin',
      'download_pdf': 'fas fa-fw fa-file-pdf',
      'download_io': 'fas fa-fw fa-file-word',
      'download_credit_note_io': 'fas fa-fw fa-file-word',
      'download': 'fas fa-fw fa-download',
      'print': 'fas fa-fw fa-print',
      'logs': 'fas fa-fw fa-history',
      'execute': 'fas fa-fw fa-play',
      'deactivate': 'fas fa-fw fa-ban',
      'reactivate': 'fas fa-fw fa-check',
      'activate': 'fas fa-fw fa-check',
      'grant': 'fas fa-fw fa-check',
      'revoke': 'fas fa-fw fa-ban',
      'select': 'fas fa-fw fa-check-square',
      'unselect': 'fas fa-fw fa-square',
      'select_all': 'fas fa-fw fa-check-double',
      'unselect_all': 'fas fa-fw fa-minus-square',
      'restriction': 'fas fa-fw fa-list',
      'remove_restriction': 'fas fa-fw fa-trash',
      'cta_closure': 'fas fa-fw fa-ban',
      'quick_edit': 'fas fa-fw fa-edit',
      'wrongly_assigned': 'fas fa-fw fa-trash',
      'invoice': 'fas fa-fw fa-file-invoice',
      'approve': 'fas fa-fw fa-check',
      'reject': 'fas fa-fw fa-ban'
    };
    return iconMap[value || ''] || 'fas fa-fw fa-ellipsis-h';
  }

  onActionClick(value: string | undefined) {
    this.action.emit({ value: value ?? '', item: this.item });
    this.visible = false;
    this.clearTargetRow();
    this.close.emit();
  }
} 